import apiClient from '@/api/client'
import type { ApiResponse } from '@/types/api'
import { getApiBaseUrl } from '@/api/base-url'

export interface Category {
  id: number
  name: string
  slug: string
  description?: string
  is_active: boolean
  sort_order: number
  parent_id?: number
  parent_name?: string
  subcategories_count: number
  products_count: number
  children?: Category[]
  created_at: string
  updated_at: string
}

export interface CategoryForm {
  name: string
  slug: string
  description?: string
  parent_id: number | null
  sort_order: number
  is_active: boolean
}

export interface CategoryStats {
  total_count: number
  root_count: number
  active_count: number
}

export interface CategoryListResponse {
  success: boolean
  data: Category[]
  meta: CategoryStats
  error?: string
  message?: string
}

export interface CategoryResponse extends ApiResponse<Category> {
  data: Category
}

export interface BulkActionRequest {
  action: 'activate' | 'deactivate' | 'delete'
  ids: number[]
}

export interface BulkActionResponse extends ApiResponse<void> {
  affected: number
}

export interface CategoryFilters {
  hierarchy?: boolean
  search?: string
  parent_id?: string
  status?: string
}

class CategoryAPI {
  private baseUrl = '/api/admin/categories'

  private buildUrl(path = ''): string {
    const base = getApiBaseUrl()
    return `${base}${this.baseUrl}${path}`
  }

  private async request<T>(
    path = '',
    options?: {
      method?: 'get' | 'post' | 'put' | 'delete'
      data?: unknown
      headers?: Record<string, string>
    },
  ): Promise<T> {
    const response = await apiClient.request<T>({
      url: this.buildUrl(path),
      method: options?.method ?? 'get',
      data: options?.data,
      ...(options?.headers !== undefined ? { headers: options.headers } : {}),
      withCredentials: true,
    })

    return response.data
  }

  /**
   * Получить список категорий
   */
  async getCategories(filters: CategoryFilters = {}): Promise<CategoryListResponse> {
    const params = new URLSearchParams()

    if (filters.hierarchy !== undefined) {
      params.append('hierarchy', filters.hierarchy.toString())
    }
    if (filters.search) {
      params.append('search', filters.search)
    }
    if (filters.parent_id) {
      params.append('parent_id', filters.parent_id)
    }
    if (filters.status) {
      params.append('status', filters.status)
    }

    const query = params.toString()
    return this.request<CategoryListResponse>(query ? `?${query}` : '')
  }

  /**
   * Получить категорию по ID
   */
  async getCategory(id: number): Promise<CategoryResponse> {
    return this.request<CategoryResponse>(`/${id}`)
  }

  /**
   * Создать новую категорию
   */
  async createCategory(data: CategoryForm): Promise<CategoryResponse> {
    return this.request<CategoryResponse>('', {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
      },
      data,
    })
  }

  /**
   * Обновить категорию
   */
  async updateCategory(id: number, data: CategoryForm): Promise<CategoryResponse> {
    return this.request<CategoryResponse>(`/${id}`, {
      method: 'put',
      headers: {
        'Content-Type': 'application/json',
      },
      data,
    })
  }

  /**
   * Удалить категорию
   */
  async deleteCategory(id: number): Promise<ApiResponse<void>> {
    return this.request<ApiResponse<void>>(`/${id}`, {
      method: 'delete',
    })
  }

  /**
   * Переместить категорию
   */
  async moveCategory(
    id: number,
    parentId: number | null,
    sortOrder?: number,
  ): Promise<CategoryResponse> {
    return this.request<CategoryResponse>(`/${id}/move`, {
      method: 'put',
      headers: {
        'Content-Type': 'application/json',
      },
      data: {
        parent_id: parentId,
        sort_order: sortOrder,
      },
    })
  }

  /**
   * Изменить порядок категорий
   */
  async reorderCategories(
    categories: Array<{ id: number; sort_order: number }>,
  ): Promise<ApiResponse<void>> {
    return this.request<ApiResponse<void>>('/reorder', {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
      },
      data: { categories },
    })
  }

  /**
   * Массовые действия с категориями
   */
  async bulkAction(data: BulkActionRequest): Promise<BulkActionResponse> {
    return this.request<BulkActionResponse>('/bulk', {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
      },
      data,
    })
  }

  /**
   * Получить дерево категорий для публичного каталога.
   * Бэкенд: GET /api/products/categories (ProductController::categories).
   */
  async getCategoriesTree(): Promise<ApiResponse<Category[]>> {
    const response = await apiClient.get<ApiResponse<Category[]>>('/api/products/categories')
    return response.data
  }

  /**
   * Поиск категорий
   */
  async searchCategories(query: string): Promise<CategoryListResponse> {
    return this.getCategories({ search: query, hierarchy: false })
  }
}

export const categoryAPI = new CategoryAPI()
export default categoryAPI
